<template>
  <div class="tabpanel__panel">
    <div class="chart-panel input-panel input-panel--wide" :class="{ 'chart-panel--highlight' : emphasizeValues }">
      <template v-if="!showChart">
        <div class="tabpanel__spinner">
          <ui-spinner/>
        </div>
      </template>

      <template v-if="showChart && pricingData">
        <h2 class="chart-panel__head mrgn-btm-5">Best Price Estimates <span>(as of {{ displayDate }})</span></h2> 

        <div class="chart-panel__actions">
          <button class="btn btn-xsm btn-secondary" 
            @click="exportPricingData"
            :disabled="pricingRequestStatus != 'DataCompleted' && pricingRequestStatus != 'Loaded'"
          >Export</button>

          <button class="btn btn-xsm btn-secondary"
            @click.prevent="activateShareModal"
            :disabled="(pricingRequestStatus != 'DataCompleted' && pricingRequestStatus != 'Loaded') || isViewCapturing"
          ><i class="fa fa-circle-o-notch fa-spin" v-show="isViewCapturing"></i> Share</button>

          <button class="btn btn-xsm btn-secondary w-100 mrgn-top-5"
            @click.prevent="toggleAlertsModal"
            :disabled="(pricingRequestStatus != 'DataCompleted' && pricingRequestStatus != 'Loaded')"
          >Set Alerts</button>

          <button class="btn btn-xsm btn-block w-100 mrgn-top-5"
            v-show="currentPricingChartLockedDate"
            @click.prevent="clearLockedDate"
          >Clear Date</button>
        </div>

        <div class="chart-panel__toggles">
          <div class="chart-panel__toggles-inner">

            <template 
              v-for="(component, key, index) in pricingData"
            >
              <div class="chart-panel__toggle" :ref="'toggle-' + component[0].pricing_component_id">

                <input type="radio" 
                  v-model="activeComponentId"
                  :id="'chart-panel-toggle-' + index"
                  :value="component[0].pricing_component_id"
                >

                <label :for="'chart-panel-toggle-' + index">
                  <span class="chart-panel__toggle-price" :data-value="formatCurrencyValue(pricingComponentValue(component[0].pricing_component_id), 4)">
                    {{ formatCurrencyValue(pricingComponentValue(component[0].pricing_component_id), 4) }}
                  </span>

                  <span class="chart-panel__toggle-component">{{ pricingComponentName(component[0].pricing_component_id) }}</span>
                </label>
                <button class="btn btn-secondary btn-xsm" aria-hidden="true" 
                  @click.prevent="toggleModal"
                  :ref="'breakdown-btn-' + component[0].pricing_component_id"
                >Price Breakdown</button>
              </div>

              <template v-if="component[0].pricing_component_id != lastComponentId">
                <div class="chart-panel__separator">+</div>
              </template>

              <template v-if="component[0].pricing_component_id == lastComponentId">
                <div class="chart-panel__separator">=</div>
              </template>
            </template>

            <div class="chart-panel__toggle" ref="toggle-total">
              <input type="radio" id="chart-panel-toggle-total" value="-1" v-model="activeComponentId">

              <label for="chart-panel-toggle-total">
                <span class="chart-panel__toggle-price" :data-value="formatCurrencyValue(totalAveragePrice, 4)">{{ formatCurrencyValue(totalAveragePrice, 4) }}</span>

                <span class="chart-panel__toggle-component">Total</span>
              </label>
              <button class="btn btn-secondary btn-xsm" aria-hidden="true" 
                @click.prevent="toggleTotalModal"
                ref="breakdown-btn-total"
              >Price Breakdown</button>
            </div>
          </div>

          <div class="chart-panel__toggle-strip">
            <button class="btn btn-secondary btn-xsm chart-panel__slide-btn fake" 
              ref="breakdown-btn-fake"
              :style="slideBtnTransform"
              @click="triggerActiveBreakdown"
            >Price Breakdown</button>
          </div>
        </div>

        <div id="pricing-chart-panel">
          <h2 class="chart-panel__head mrgn-btm-0">Historical {{ chartTitle }} Prices</h2> 

          <div
            @mouseenter="emphasizeValues = true"
            @mouseleave="emphasizeValues = false"
          >
            <pricing-chart 
              v-if="activeComponentId && activeComponentId >= 0" 
              :full-pricing-data="pricingData"
              :pricing-data="pricingData[activeComponentId]" 
              :pricing-component="activeComponent"
              :term="term"
              :term-usage="termUsage"
              :display-total="false"
              :chart-locked-date="currentPricingChartLockedDate"
              @lockDate="lockDate"
            ></pricing-chart>

            <pricing-chart 
              v-if="activeComponentId && activeComponentId == -1" 
              :full-pricing-data="pricingData"
              :pricing-data-totals="pricingDataTotals"
              :pricing-component="activeComponent"
              :term="term"
              :term-usage="termUsage"
              :display-total="true"
              :chart-locked-date="currentPricingChartLockedDate"
              @lockDate="lockDate"
            ></pricing-chart>
          </div>
        </div>

        <component-modal
          v-if="activeComponentId != -1" 
          :modal-visible="modalVisible"
          :component-id="activeComponentId"
          :date="displayDate"
          :request-id="currentPricingDataRequestId"
          :term="term"
          :term-usage="termUsage"
          :broker-fee="brokerFee"
          @toggleModal="toggleModal"
        ></component-modal>

        <share-modal 
          :request-id="currentPricingDataRequestId"
          :term="term"
          :component-id="activeComponentId"
          :date="shareDate"
          :chart-locked-date="currentPricingChartLockedDate"
          :share-modal-visible="shareModalVisible"
          @toggleModal="toggleShareModal"
        ></share-modal>

        <total-modal 
          v-if="activeComponentId == -1"
          :total-modal-visible="totalModalVisible"
          :component-id="activeComponentId"
          :date="displayDate"
          :request-id="currentPricingDataRequestId"
          :pricing-data="pricingData" 
          :pricing-data-totals="pricingDataTotals" 
          :term="term"
          :term-usage="termUsage"
          @toggleModal="toggleTotalModal"
        ></total-modal>

        <alerts-modal 
          :modal-visible="alertsModalVisible"
          :pricing-request-id="currentPricingRequest.savedPricingRequestId"
          :pricing-data="pricingData" 
          @toggleModal="toggleAlertsModal"
          @closeModal="closeAlertsModal"
        ></alerts-modal>

        <export-modal 
          :export-modal-visible="exportModalVisible"
          :dataRequestId="currentPricingDataRequestId"
          @toggleModal="toggleExportModal"
        ></export-modal>

      </template>

      <ul class="legend" v-show="showChart">
        <li class="legend__item legend__item--best">Best Estimate</li>
        <li class="legend__item">High/Low Estimate</li>
      </ul>
    </div>
  </div>
</template>

<script>
import { nextTick } from 'vue'
import moment from 'moment'
import swal from 'sweetalert'
import ComponentDataModal from './ComponentDataModal.vue'
import ShareModal from './ShareModal.vue'
import TotalModal from './TotalDataModal.vue'
import AlertsModal from './AlertsModal.vue'
import ExportModal from './ExportModal.vue'
import PricingChart from './PricingChart.vue'
import UiSpinner from '../../../../common/components/UiSpinner.vue'
import EventBus from '../../../../common/event-bus.js'
import { formatCurrency, offset } from '../../../../common/helpers'

export default {
  props: ['term', 'pricingData', 'pricingDataTotals'],
  components: {
    'pricing-chart': PricingChart,
    'component-modal': ComponentDataModal,
    'share-modal': ShareModal,
    'total-modal': TotalModal,
    'alerts-modal': AlertsModal,
    'export-modal': ExportModal,
    'ui-spinner': UiSpinner,
  },
  data() {
    return {
      activeComponentId: 0,
      modalVisible: false,
      shareModalVisible: false,
      totalModalVisible: false,
      alertsModalVisible: false,
      exportModalVisible: false,
      emphasizeValues: false,
      isViewCapturing: false,
      slideBtnTransform: '',
    }
  },
  computed: {
    currentPricingRequest() {
      return this.$store.getters.currentPricingRequest
    },
    dashboardSortUtility() {
      return this.$store.getters.dashboardSortUtility
    },
    isLoggedIn() {
      return this.$store.state.auth && 
             this.$store.state.auth.user &&
             this.$store.state.auth.user.id
    },
    userPlan() {
      return this.$store.getters.userPlan
    },
    planConfig() {
      return this.$store.getters.planConfig
    },
    pricingRequestStatus() {
      return this.$store.getters.pricingRequestStatus
    },
    currentPricingChartData() {
      return this.$store.getters.currentPricingChartData
    },
    currentPricingChartLockedDate() {
      return this.$store.getters.currentPricingChartLockedDate
    },
    pricingComponents() {
      return this.$store.getters.dashboardPricingComponents
    },
    currentPricingDataRequestId() {
      return this.$store.getters.currentPricingDataRequestId
    },
    currentBatch() {
      return this.$store.getters.currentBatch
    },
    activeComponent() {
      if (this.activeComponentId) {
        return this.pricingComponents.find(component => component.id == this.activeComponentId)
      }
    },
    activeComponentName() {
      if (this.activeComponent) {
        return this.activeComponent.name
      }
    },
    chartTitle() {
      if (this.activeComponentId) {
        let chartTitle = this.pricingComponentChartTitle(this.activeComponentId * 1)

        if (!chartTitle) {
          chartTitle = this.pricingComponentName(this.activeComponentId * 1)
        }

        return chartTitle
      }
    },
    firstComponentId() {
      if (this.pricingData && Object.keys(this.pricingData).length) {
        let componentsArray = Object.keys(this.pricingData).map(item => item)
        return componentsArray[0]
      }
    },
    lastComponentId() {
      if (this.pricingData && Object.keys(this.pricingData).length) {
        let componentsArray = Object.keys(this.pricingData).map(item => item)
        return componentsArray[componentsArray.length - 1]
      }
    },
    totalAveragePrice() {
      if (
        this.pricingDataTotals && 
        Object.keys(this.currentPricingChartData).length &&
        this.currentPricingChartData[0]
      ) {
        let date = moment(this.currentPricingChartData[0].date).format('YYYY-MM-DD')
        let avgPrice

        if (this.pricingDataTotals[date]) {
          avgPrice = (this.pricingDataTotals[date].avg / this.termUsage) + (this.pricingDataTotals[date].broker_fee * 1)
        } else {
          avgPrice = this.pricingDataTotals[Object.keys(this.pricingDataTotals)[0]]
        }

        return avgPrice
      }
    },
    termUsage() {
      if (this.firstComponentId && this.pricingData) {
        return this.pricingData[this.firstComponentId][0].term_usage
      }
    },
    brokerFee() {
      // Pull the broker free from the first item of 
      // the active pricing component's data.
      if (this.firstComponentId && this.pricingData) {
        if (this.pricingData[this.activeComponentId] && this.pricingData[this.activeComponentId][0].broker_fee) {
          return this.pricingData[this.activeComponentId][0].broker_fee
        }
      }
      return 0
    },
    storeBrokerFee() {
      let brokerFee = null

      if (this.currentPricingRequest.volume.brokerFee) {
        brokerFee = this.currentPricingRequest.volume.brokerFee

      } else if (this.currentPricingRequest.customFacilityVolume.brokerFee) {
        brokerFee = this.currentPricingRequest.customFacilityVolume.brokerFee
      }

      return brokerFee
    },
    dates() {
      if (this.firstComponentId && this.pricingData) {
        return this.pricingData[this.firstComponentId].map(item => moment(item.date).format('M/DD/YY')).reverse()
      }
    },
    latestDate() {
      if (this.dates) {
        return this.dates[this.dates.length - 1]
      }
    },
    displayDate() {
      let date = 'YYYY-MM-DD'

      if (Object.keys(this.currentPricingChartData).length) {
        date = moment(this.currentPricingChartData[0].date).format('M/DD/YYYY')
      }

      return date
    },
    shareDate() {
      if (this.displayDate && this.displayDate != 'YYYY-MM-DD') {
        return moment(this.displayDate, 'M/DD/YYYY').format('YYYY-MM-DD')
      }
    },
    showChart() {
      if (this.pricingRequestStatus === 'Loaded') {
        return true
      }

      if (this.currentBatch < 2) {
        return false
      }

      return true
    }
  },
  methods: {
    setSlideBtnTransform() {
      this.slideBtnTransform = this.getSlideBtnTransform()
    },
    getSlideBtnTransform() {
      // Current x position of the fake button.
      let breakdownFakeButton = this.$refs['breakdown-btn-fake']
      let breakdownFakeButtonX = 0
      if (breakdownFakeButton) {
        breakdownFakeButtonX = offset(breakdownFakeButton).left
      }

      // Number of pixels the fake button has moved to the right.
      let currentTranslateX = 0
      if (breakdownFakeButton) {
        let breakdownFakeButtonTransform = breakdownFakeButton.style.transform
        if (breakdownFakeButtonTransform != 'none') {
          currentTranslateX = parseInt(breakdownFakeButtonTransform.replace(/[^\d.]/g, ''))
        }
      }

      // Calculate the initial x position of the fake button.
      let initialFakeButtonX = breakdownFakeButtonX - currentTranslateX

      // Current x position of the selected component button.
      let breakdownButtonX = 0
      if (this.activeComponentId > -1) {
        let breakdownButton = this.$refs['breakdown-btn-' + this.activeComponentId]

        if (breakdownButton && breakdownButton[0]) {
          breakdownButtonX = offset(breakdownButton[0]).left
        }
      } else {
        let totalButton = this.$refs['breakdown-btn-total']
        if (totalButton) {
          breakdownButtonX = offset(totalButton).left
        }
      }

      // Calculate the number of pixels between the selected component button
      // and the initial x position of the fake button, which is number
      // of pixel we need to slide the fake button (from its initial x position).
      let diff = Math.floor(breakdownButtonX - initialFakeButtonX)

      if (diff > 0) {
        return "transform: translateX(" + diff + "px);"
      }

      return "transform: translateX(0px);"
    },
    pricingComponentName(componentId) {
      if (componentId === -1) {
        return 'Total'
      }

      let component = this.pricingComponents.filter(component => component.id === componentId)
      if (component.length) {
        return component[0].name
      }
    },
    pricingComponentChartTitle(componentId) {
      if (componentId === -1) {
        return 'Total'
      }

      let component = this.pricingComponents.filter(component => component.id === componentId)
      if (component.length) {
        return component[0].meta?.chartTitle
      }
    },
    pricingComponentValue(componentId) {
      let value = 0,
          chartData

      if (Object.keys(this.currentPricingChartData).length) {
        chartData = this.currentPricingChartData.filter(item => item?.pricing_component_id === componentId)[0]

        if (chartData) {
          value = (chartData.avg / chartData.term_usage) + (chartData.broker_fee * 1)
        }
      }

      return value
    },
    toggleModal() {
      this.modalVisible = !this.modalVisible
    },
    activateShareModal() {
      
      // Only allow sharing if using Chrome since there are issues
      // with screen capturing on other browsers.
      const browserIsChrome = navigator.userAgent.indexOf('Chrome') > -1
      if (! browserIsChrome) {
        let swalContent = document.createElement('div')
        swalContent.innerHTML = '<p class="alerts message alerts--no-brdr mrgn-btm-5">Share functionality is currently only<br> supported in Google Chrome</p>'

        swal({
          content: swalContent,
          animation: false
        })
        return
      }

      this.isViewCapturing = true

      EventBus.$on('workspaceCaptured', () => {
        this.shareModalVisible = true
        this.isViewCapturing = false
      })
      EventBus.$emit('captureWorkspace')
    },
    toggleShareModal() {
      this.shareModalVisible = !this.shareModalVisible
    },
    toggleTotalModal() {
      this.totalModalVisible = !this.totalModalVisible
    },
    toggleAlertsModal() {

      if (! this.isLoggedIn) {
        return this.showPaywallModal()
      }

      this.alertsModalVisible = !this.alertsModalVisible
    },
    closeAlertsModal() {
      this.alertsModalVisible = false
    },
    toggleExportModal() {
      this.exportModalVisible = !this.exportModalVisible
    },
    loadComponentData() {
      if (this.pricingData) {
        let pricingData = _.cloneDeep(this.pricingData)
        let chartComponentData =  []

        for (let [key, value] of Object.entries(pricingData)) {
          let componentData = []

          if (this.displayDate && this.displayDate != 'YYYY-MM-DD') {
            componentData = pricingData[key].filter(item => moment(item.date).format('M/DD/YYYY') === this.displayDate)
          } else {
            componentData = pricingData[key].filter(item => moment(item.date).format('M/DD/YY') === this.latestDate)
          }

          if (componentData.length) {
            chartComponentData.push(componentData[0])
          }
        }

        if (chartComponentData.length) {
          this.$store.dispatch('setCurrentPricingChartData', chartComponentData)
        }

        nextTick(() => {
          this.setSlideBtnTransform()
        })
      }
    },
    exportPricingData() {
      if (! this.currentPricingDataRequestId) {
        return
      }

      if (! this.planConfig || ! this.planConfig.exportable) {
        return this.showPaywallModal()
      }

      // window.open('/pricing-calculator/me/data/requests/' + this.currentPricingDataRequestId + '/export')
      this.toggleExportModal()
    },
    showPaywallModal() {
      let msg = '<a href="/subscribe">Subscription</a> required';
      if (this.dashboardSortUtility.name) {
        msg += ' for this utility:<br>' + this.dashboardSortUtility.name
      }

      let swalContent = document.createElement('div')
      swalContent.innerHTML = '<br><p style="margin-bottom: -20px;">' + msg + '</p>'

      swal({
        content: swalContent,
        closeOnEsc: false,
        closeOnClickOutside: false,
        buttons: {
          confirm: {
            text: "OK",
            value: true,
            visible: true,
            className: "",
            closeModal: true,
          }
        }
      })
    },
    triggerActiveBreakdown() {
      if (this.activeComponentId > -1) {
        let breakdownButton = this.$refs['breakdown-btn-' + this.activeComponentId]
        if (breakdownButton) {
          breakdownButton[0].click()
        }
      } else {
        let breakdownTotalButton = this.$refs['breakdown-btn-total']
        if (breakdownTotalButton) {
          breakdownTotalButton.click()
        }
      }
    },
    /**
     * Set the chart's locked date.
     */
    lockDate(date) {
      this.$store.dispatch('setCurrentPricingChartLockedDate', date)
    },
    /**
     * Clear the chart's locked date.
     */
    clearLockedDate() {
      this.$store.dispatch('setCurrentPricingChartLockedDate', undefined)
    },
    formatCurrencyValue(val, decimals) {
      return formatCurrency(val, decimals)
    }
  },
  watch: {
    activeComponentId() {
      this.setSlideBtnTransform()
    },
    firstComponentId() {
      this.activeComponentId = this.firstComponentId

      if (this.firstComponentId) {
        this.loadComponentData()
      }
    },
    term() {
      this.loadComponentData()
    }
  },
  mounted() {
    if (this.firstComponentId) {
      this.activeComponentId = this.firstComponentId
    }

    nextTick(() => {
      this.setSlideBtnTransform()
    })
  },
}
</script>
